import { Box, Container, TableCell, TableRow } from '@mui/material';
import { INVOICE_STATUS_CLOSED, INVOICE_STATUS_DEBT, INVOICE_STATUS_NEW, INVOICE_STATUS_PAID, INVOICE_STATUS_PARTIAL_PAID, INVOICE_STATUS_WCC_CREATED, ProfitDto, TurnoverDto, ReportSaleManagerDto } from '@sr/dto';
import { ErrorBanner } from 'shared/ui/error-banner';
import { GridLayout } from 'shared/ui/grid-layout';
import PriceSpan from 'shared/ui/price-span';
import { LoadingBanner } from 'shared/ui/loading-banner';

const columns = ['Менеджер','Выписан', 'Оплачен', 'Долг', 'Частично оплачен', 'Закрыт', 'Выставлен акт', 'Итого'];

// Показанные статусы (без отменённых) — тот же набор, что и в сортировке на сервере
const TURNOVER_SHOWN_PREFIXES = ['NEW', 'PAID', 'DEBT', 'PARTIAL_PAID', 'CLOSED', 'WCC_CREATED'] as const;
const PROFIT_SHOWN_STATUSES = [INVOICE_STATUS_NEW, INVOICE_STATUS_PAID, INVOICE_STATUS_DEBT, INVOICE_STATUS_PARTIAL_PAID, INVOICE_STATUS_CLOSED, INVOICE_STATUS_WCC_CREATED] as const;

const turnoverSumOf = (row: TurnoverDto) => TURNOVER_SHOWN_PREFIXES.reduce((acc, k) => acc + Number(row[`INVOICE_STATUS_${k}_SUM`]), 0);
const turnoverCountOf = (row: TurnoverDto) => TURNOVER_SHOWN_PREFIXES.reduce((acc, k) => acc + Number(row[`INVOICE_STATUS_${k}_COUNT`]), 0);
const profitOf = (row: ProfitDto) => PROFIT_SHOWN_STATUSES.reduce((acc, st) => acc + Number(row.profitByStatus[st]), 0);

type Props = {
    collection: ReportSaleManagerDto,
    isLoading: boolean,
    error: string,
}

export const SalesGrid = ({ collection, isLoading, error }: Props ) => {
  if (error)
    return (
      <Container>
        <ErrorBanner errorMessage={error} />
      </Container>
    );
  if (isLoading) {
    return <LoadingBanner />;
  }
  return (
    <>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '80vh',
        p: 2 
      }}>
        <h3>Оборот по счетам</h3>
        <GridLayout
          flex={1}
          columns={columns}
          items={collection.turnover}
          isLoading={isLoading}
          error={error}
          footer={TurnoverTotal}
          itemRender={(row) => <TurnoverRow row={row} />}
          noItemsText="Нет счетов за выбранный период" />
        <br />
        <h3>Прибыль по счетам</h3>  
        <GridLayout
          flex={1}
          columns={columns}
          items={collection.profit}
          isLoading={isLoading}
          error={error}
          footer={ProfitTotal}
          itemRender={(row) => <ProfitRow row={row} />}
          noItemsText="Нет счетов за выбранный период" />
      </Box>
    </>
    
      
  );
};
const TurnoverRow = ({ row }: { row: TurnoverDto }) => {
  return (
    <TableRow>
      <TableCell align="left">{row.fullName}</TableCell>
      <TableCell align="center"><PriceSpan price={row.INVOICE_STATUS_NEW_SUM} size="small" /> ({row.INVOICE_STATUS_NEW_COUNT} шт.)</TableCell>
      <TableCell align="center"><PriceSpan price={row.INVOICE_STATUS_PAID_SUM} size="small" /> ({row.INVOICE_STATUS_PAID_COUNT} шт.)</TableCell>
      <TableCell align="center"><PriceSpan price={row.INVOICE_STATUS_DEBT_SUM} size="small" /> ({row.INVOICE_STATUS_DEBT_COUNT} шт.)</TableCell>
      <TableCell align="center"><PriceSpan price={row.INVOICE_STATUS_PARTIAL_PAID_SUM} size="small" /> ({row.INVOICE_STATUS_PARTIAL_PAID_COUNT} шт.)</TableCell>
      <TableCell align="center"><PriceSpan price={row.INVOICE_STATUS_CLOSED_SUM} size="small" /> ({row.INVOICE_STATUS_CLOSED_COUNT} шт.)</TableCell>
      <TableCell align="center"><PriceSpan price={row.INVOICE_STATUS_WCC_CREATED_SUM} size="small" /> ({row.INVOICE_STATUS_WCC_CREATED_COUNT} шт.)</TableCell>
      <TableCell align="center"><b><PriceSpan price={turnoverSumOf(row)} size="small" /> ({turnoverCountOf(row)} шт.)</b></TableCell>
    </TableRow>);
};

const TurnoverTotal = (rows: TurnoverDto[]) => {
  return (
    <TableRow>
      <TableCell align="center">
        <b>ИТОГО:</b>
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.INVOICE_STATUS_NEW_SUM) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.INVOICE_STATUS_PAID_SUM) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.INVOICE_STATUS_DEBT_SUM) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.INVOICE_STATUS_PARTIAL_PAID_SUM) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.INVOICE_STATUS_CLOSED_SUM) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.INVOICE_STATUS_WCC_CREATED_SUM) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <b><PriceSpan price={rows.reduce((acc, curr ) => turnoverSumOf(curr) + acc, 0)} size="regular" /> ({rows.reduce((acc, curr ) => turnoverCountOf(curr) + acc, 0)} шт.)</b>
      </TableCell>
    </TableRow>

  );
};

const ProfitRow = ({ row }: { row: ProfitDto }) => {
  return (
    <TableRow>
      <TableCell align="left">{row.fullName}</TableCell>
      <TableCell align="center"><PriceSpan price={row.profitByStatus[INVOICE_STATUS_NEW]} size="small" /></TableCell>
      <TableCell align="center"><PriceSpan price={row.profitByStatus[INVOICE_STATUS_PAID]} size="small" /></TableCell>
      <TableCell align="center"><PriceSpan price={row.profitByStatus[INVOICE_STATUS_DEBT]} size="small" /></TableCell>
      <TableCell align="center"><PriceSpan price={row.profitByStatus[INVOICE_STATUS_PARTIAL_PAID]} size="small" /></TableCell>
      <TableCell align="center"><PriceSpan price={row.profitByStatus[INVOICE_STATUS_CLOSED]} size="small" /></TableCell>
      <TableCell align="center"><PriceSpan price={row.profitByStatus[INVOICE_STATUS_WCC_CREATED]} size="small" /></TableCell>
      <TableCell align="center"><b><PriceSpan price={profitOf(row)} size="small" /></b></TableCell>
    </TableRow>);
};

const ProfitTotal = (rows: ProfitDto[]) => {
  return (
    <TableRow>
      <TableCell align="center">
        <b>ИТОГО:</b>
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.profitByStatus[INVOICE_STATUS_NEW]) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.profitByStatus[INVOICE_STATUS_PAID]) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.profitByStatus[INVOICE_STATUS_DEBT]) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.profitByStatus[INVOICE_STATUS_PARTIAL_PAID]) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.profitByStatus[INVOICE_STATUS_CLOSED]) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <PriceSpan price={rows.reduce((acc, curr ) => Number(curr.profitByStatus[INVOICE_STATUS_WCC_CREATED]) + acc, 0)} size="regular" />
      </TableCell>
      <TableCell align="center">
        <b><PriceSpan price={rows.reduce((acc, curr ) => profitOf(curr) + acc, 0)} size="regular" /></b>
      </TableCell>
    </TableRow>
  );
};
